import { ErrorApi, ErrorNotLoggedIn, Preloader } from 'src/components'
import { savedTokens } from 'src/auth'
import { useGetOrganization } from '../Info/queries'
import {
  TrackedTextField,
  TrackedSaveButton,
  TrackedToggle,
} from '../Info/TrackedInputs'
import {
  savedSfButtonEnabled,
  savedSfButtonKey,
  savedSfCallLogging,
  savedSfLoginUrl,
  savedSfPrincipalArn,
  savedSfRoleArn,
  savedSfClientId,
  savedSfClientSecret,
  savedSfAuthUrl,
  savedSfEndpoint,
  savedSfPassword,
  savedSfUsername,
} from '../Info/storage'
import { toast } from 'sonner'
import CrmAttributeImage from 'src/images/crm_attribute.png'
import CrmButtonImage from 'src/images/crm_button.png'

type Props = {
  organization: string
  username: string
}

function Config(props: Props) {
  const { status, data, error, refetch } = useGetOrganization({
    organization: props.organization,
    username: props.username,
  })

  if (status === 'loading' || status === 'idle') {
    return <Preloader />
  }

  if (status === 'error') {
    return <ErrorApi message={error} onClick={refetch} />
  }

  if (data.setupStep !== 'finished') {
    toast.warning(`You didn't finish the setup yet.`)
    return null // FIXME: how to handle this case?
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-6 px-6 mt-3">
        <TrackedToggle
          savedStorage={savedSfButtonEnabled}
          label="Show Salesforce button"
          title="application.sf_button_enabled"
          initialValue={data.application.sf_button_enabled}
          tooltip={
            <div className="flex flex-row gap-5">
              <img
                className="w-[186px] object-fit"
                src={CrmButtonImage}
                alt="CRM button in app"
              />
              <div className="flex flex-col gap-5">
                <p>
                  show/hide a CRM button in the app during an interaction. The
                  button will only be visible if an actual url is present in the
                  contact attributes.
                </p>
              </div>
            </div>
          }
        />

        <TrackedTextField
          trimValue
          savedStorage={savedSfButtonKey}
          label="Salesforce Button Key"
          title="application.sf_button_key"
          initialValue={data.application.sf_button_key}
          tooltip={
            <div className="flex flex-row gap-5">
              <img
                className="w-[421px] object-fit"
                src={CrmAttributeImage}
                alt="Contact Attribute"
              />
              <div className="flex flex-col gap-5">
                <p>
                  Add the key of the contact attribute you've set up within
                  Amazon Connect (routing -&gt; flows -&gt; set contact
                  attributes) below to configure which url the user can open
                  from within the app.
                </p>
              </div>
            </div>
          }
        />

        <TrackedToggle
          savedStorage={savedSfCallLogging}
          label="Enable Salesforce call logging"
          title="application.salesforceEnabled"
          initialValue={data.application.salesforceEnabled}
        />

        <TrackedTextField
          trimValue
          savedStorage={savedSfLoginUrl}
          label="Salesforce Login Url"
          title="sso.loginUrl"
          initialValue={data.sso?.loginUrl ?? ''}
          placeholder="https://INSTANCE.my.salesforce.com/idp/login?app=ID"
          type="url"
        />

        <TrackedTextField
          trimValue
          savedStorage={savedSfPrincipalArn}
          label="Salesforce Principal Arn"
          title="sso.principalArn"
          placeholder="arn:aws:iam::ACCOUNT_ID:saml-provider/PROVIDER_ID"
          pattern="arn:aws:iam::\d{12}:saml-provider\/[A-Za-z0-9\-_]+"
          initialValue={data.sso?.principalArn ?? ''}
        />

        <TrackedTextField
          trimValue
          savedStorage={savedSfRoleArn}
          label="Salesforce Role Arn"
          placeholder="arn:aws:iam::ACCOUNT_ID:role/ROLE_ID"
          pattern="arn:aws:iam::\d{12}:role\/[A-Za-z0-9\-_]+"
          title="sso.roleArn"
          initialValue={data.sso?.roleArn ?? ''}
        />

        <TrackedTextField
          trimValue
          savedStorage={savedSfClientId}
          label="Salesforce Client Id"
          title="application.sf_client_id"
          initialValue={data.application.sf_client_id}
          placeholder="client id"
        />

        <TrackedTextField
          trimValue
          savedStorage={savedSfClientSecret}
          label="Salesforce Client Secret"
          title="application.sf_client_secret"
          initialValue={data.application.sf_client_secret}
          type="password"
          placeholder="client secret"
        />

        <TrackedTextField
          trimValue
          savedStorage={savedSfAuthUrl}
          label="Salesforce Auth Url"
          title="application.sf_url"
          initialValue={data.application.sf_url}
          type="url"
        />

        <TrackedTextField
          trimValue
          savedStorage={savedSfEndpoint}
          label="Salesforce Endpoint"
          title="application.sf_endpoint"
          initialValue={data.application.sf_endpoint}
          type="url"
        />

        <TrackedTextField
          trimValue
          savedStorage={savedSfUsername}
          label="Salesforce Username"
          title="application.sf_username"
          initialValue={data.application.sf_username}
          placeholder="john.doe@gmail.com"
        />

        <TrackedTextField
          trimValue
          savedStorage={savedSfPassword}
          label="Salesforce Password"
          title="application.sf_password"
          initialValue={data.application.sf_password}
          type="password"
          placeholder="password"
        />
      </div>

      <TrackedSaveButton
        organization={props.organization}
        username={props.username}
      />
    </>
  )
}

export default function ValidateInfo() {
  const [tokens] = savedTokens.useState(null)

  if (!tokens) {
    return <ErrorNotLoggedIn />
  }

  return (
    <Config organization={tokens.organization} username={tokens.username} />
  )
}
